import dayjs from 'dayjs';

/** 보험사 코드 */
export const INSURANCE_COMPANY_CODES = {
  MERITZ: '01',
  HANWHA: '02',
  LOTTE: '03',
  MG: '04',
  HEUNGKUK: '05',
  SAMSUNG: '08',
  HYUNDAI: '09',
  KB: '10',
  DB: '13',
  ACE: '18',
  AXA: '41',
  BNP: '43',
  CARROT: '69',
  NH: '71',
};

/** 성능보험(일반)만 조회 — Safe6 제외 */
export const PERFORMANCE_INSURANCE_ONLY_LOGIN_IDS = [
  'db001',
  'db002',
  'meritz001',
];

/** Safe6만 조회 */
export const SAFE6_ONLY_LOGIN_IDS = ['safedb', 'safekb', 'safemeritz'];

/** 시작일 제한 데모 계정 (2026-06-01 이전 선택 불가) */
export const SAFE_INSURANCE_DEMO_LOGIN_IDS = ['db001', 'db002', 'safekb', 'safedb'];

/** 시작일만 제한 (2026-06-01 이전 선택 불가) */
export const INSURANCE_MIN_DATE_LOGIN_IDS = ['meritz001', 'safemeritz'];

/** 데모 계정 최소 검색 시작일 */
export const INSURANCE_MIN_SEARCH_DATE = dayjs('2026-06-01').toDate();

/** @deprecated INSURANCE_MIN_SEARCH_DATE 사용 */
export const SAFE_INSURANCE_DEMO_DATE_RANGE = {
  start: INSURANCE_MIN_SEARCH_DATE,
};

/** 로그인 ID별 조회 가능 보험사 코드 */
export const LOGIN_ID_INSURANCE_CODE = {
  db001: INSURANCE_COMPANY_CODES.KB,
  safekb: INSURANCE_COMPANY_CODES.KB,
  db002: INSURANCE_COMPANY_CODES.DB,
  safedb: INSURANCE_COMPANY_CODES.DB,
  meritz001: INSURANCE_COMPANY_CODES.MERITZ,
  safemeritz: INSURANCE_COMPANY_CODES.MERITZ,
};

/** 보험사용 데모 6계정 (KB/메리츠/DB × 일반·Safe6) */
export const INSURANCE_DEMO_LOGIN_IDS = Object.keys(LOGIN_ID_INSURANCE_CODE);

/**
 * 보험사 데모 계정 — 업무처리상태 블라인드 (입고안내완료 Z05 이전·서류보완요청)
 * 노출: Z05(입고안내완료) 이후
 */
export const INSURANCE_DEMO_BLIND_CLAIM_STATUSES = [
  'Z01', // 고객접수중
  'Z02', // 서류접수(고객/매매상사) 대기중
  'Z03', // 서류접수(고객/매매상사) 완료
  'Z04', // 거절안내완료
  'Z21', // 고객서류 재요청
  'Z22', // 매매상사서류 재요청
  'Z23', // 고객+매매상사서류 재요청
  'Z24', // 고객서류보완완료/매매상사서류보완요청
  'Z25', // 고객서류보완요청/매매상사서류보완완료
];

/** 사고관리 메뉴가 없을 때 주입할 메뉴 */
export const INSURANCE_DEMO_ACCIDENT_MENU = {
  label: '보상관리',
  icon: 'pi pi-fw pi-briefcase',
  items: [
    {
      label: '사고관리',
      icon: 'pi pi-fw pi-car',
      to: '/compensation/accident/list',
    },
  ],
};

export const isInsuranceDemoAccount = (loginId) =>
  INSURANCE_DEMO_LOGIN_IDS.includes(loginId);

/** 삭제된 클레임 사진/서류 여부 */
export const isDeletedClaimDocument = (file) =>
  !file ||
  file.documentCode === 'C_DELETE' ||
  !!file.delTime ||
  file.state === 'D';

/**
 * 보험사 데모 6계정 다운로드 시 삭제된 사진/서류 제외
 * @param {Array} files
 * @param {string} loginId
 */
export const filterClaimDocsForDownload = (files, loginId) => {
  if (!files?.length) return files ?? [];
  if (!isInsuranceDemoAccount(loginId)) return files;
  return files.filter((file) => !isDeletedClaimDocument(file));
};

export const isInsuranceDemoBlindClaimStatus = (status) =>
  INSURANCE_DEMO_BLIND_CLAIM_STATUSES.includes(status);

export const filterClaimStatusesForInsuranceDemo = (statusList, loginId) => {
  if (!isInsuranceDemoAccount(loginId) || !statusList?.length) {
    return statusList ?? [];
  }
  return statusList.filter(
    (item) => !isInsuranceDemoBlindClaimStatus(item.value)
  );
};

export const resolveInsuranceDemoCheckType = (
  checkType,
  loginId,
  visibleStatusValues
) => {
  if (!isInsuranceDemoAccount(loginId)) return checkType;
  if (checkType == null || checkType === '') {
    return visibleStatusValues?.length ? visibleStatusValues : checkType;
  }
  if (Array.isArray(checkType)) {
    const filtered = checkType.filter(
      (status) => !isInsuranceDemoBlindClaimStatus(status)
    );
    return filtered.length
      ? filtered
      : visibleStatusValues?.length
        ? visibleStatusValues
        : null;
  }
  return isInsuranceDemoBlindClaimStatus(checkType)
    ? visibleStatusValues?.length
      ? visibleStatusValues
      : null
    : checkType;
};

const menuHasAccidentPath = (menus) =>
  (menus ?? []).some(
    (menu) =>
      menu.to === '/compensation/accident/list' ||
      menu.to === '/compensation/accident' ||
      menu.items?.some(
        (item) =>
          item.to === '/compensation/accident/list' ||
          item.to === '/compensation/accident'
      )
  );

/** 보험사 데모 계정에 사고관리 메뉴가 없으면 추가 */
export const ensureInsuranceDemoAccidentMenu = (menus, loginId) => {
  if (!isInsuranceDemoAccount(loginId)) return menus ?? [];
  if (menuHasAccidentPath(menus)) return menus ?? [];
  return [...(menus ?? []), INSURANCE_DEMO_ACCIDENT_MENU];
};

export const isSafeInsuranceDemoUser = (loginId) =>
  SAFE_INSURANCE_DEMO_LOGIN_IDS.includes(loginId);

export const isInsuranceMinDateUser = (loginId) =>
  INSURANCE_MIN_DATE_LOGIN_IDS.includes(loginId);

export const hasInsuranceDateRestriction = (loginId) =>
  isSafeInsuranceDemoUser(loginId) || isInsuranceMinDateUser(loginId);

export const getInsuranceCompanyCodeByLoginId = (loginId) =>
  LOGIN_ID_INSURANCE_CODE[loginId] ?? null;

const normalizeInsuranceCode = (code) => {
  if (code == null || code === '') return null;
  return String(code).padStart(2, '0');
};

/** @returns {'performance'|'safe6'|null} */
export const getInsuranceDemoAssuranceFilter = (loginId) => {
  if (!loginId) return null;
  if (PERFORMANCE_INSURANCE_ONLY_LOGIN_IDS.includes(loginId)) {
    return 'performance';
  }
  if (SAFE6_ONLY_LOGIN_IDS.includes(loginId)) {
    return 'safe6';
  }
  return null;
};

export const filterClaimsByInsuranceDemoAccess = (list, loginId) => {
  if (!list?.length) return list ?? [];

  let filtered = list;

  const assuranceFilter = getInsuranceDemoAssuranceFilter(loginId);
  if (assuranceFilter === 'performance') {
    filtered = filtered.filter((item) => item.assuranceType !== '6');
  } else if (assuranceFilter === 'safe6') {
    filtered = filtered.filter((item) => item.assuranceType === '6');
  }

  const insuranceCode = getInsuranceCompanyCodeByLoginId(loginId);
  if (insuranceCode) {
    const normalized = normalizeInsuranceCode(insuranceCode);
    filtered = filtered.filter(
      (item) => normalizeInsuranceCode(item.insuranceCode) === normalized
    );
  }

  if (isInsuranceDemoAccount(loginId)) {
    filtered = filtered.filter(
      (item) => !isInsuranceDemoBlindClaimStatus(item.claimStatus)
    );
  }

  return filtered;
};

export const needsClientSideClaimFilter = (loginId) =>
  !!getInsuranceDemoAssuranceFilter(loginId) ||
  !!getInsuranceCompanyCodeByLoginId(loginId);

const CLAIM_FETCH_BATCH_SIZE = 10000;

export const paginateFilteredClaims = (filteredList, page, size) => {
  const pageNum = Math.max(1, Number(page) || 1);
  const pageSize = Math.max(1, Number(size) || 100);
  const start = (pageNum - 1) * pageSize;
  return {
    list: filteredList.slice(start, start + pageSize),
    total: filteredList.length,
  };
};

export const fetchAllClaimsWithDemoFilter = async (
  getListFn,
  searchParams,
  loginId
) => {
  let allList = [];
  let apiTotal = 0;
  let currentPage = 1;

  while (true) {
    const data = await getListFn({
      ...searchParams,
      page: currentPage,
      size: CLAIM_FETCH_BATCH_SIZE,
    });
    if (!data?.list?.length) break;
    allList = allList.concat(data.list);
    apiTotal = data.total ?? allList.length;
    if (
      allList.length >= apiTotal ||
      data.list.length < CLAIM_FETCH_BATCH_SIZE
    ) {
      break;
    }
    currentPage += 1;
  }

  return filterClaimsByInsuranceDemoAccess(allList, loginId);
};

export const resolveClaimListResult = async (
  getListFn,
  apiParams,
  loginId,
  cache,
  { forceRefetch = false } = {}
) => {
  const page = apiParams.page ?? 1;
  const size = apiParams.size ?? 100;

  if (!needsClientSideClaimFilter(loginId)) {
    const data = await getListFn(apiParams);
    return { list: data?.list ?? [], total: data?.total ?? 0 };
  }

  const { page: _page, size: _size, ...searchParams } = apiParams;
  const paramsKey = JSON.stringify(searchParams);

  if (forceRefetch || cache.paramsKey !== paramsKey || !cache.filteredList) {
    cache.paramsKey = paramsKey;
    cache.filteredList =
      (await fetchAllClaimsWithDemoFilter(getListFn, searchParams, loginId)) ??
      [];
  }

  return paginateFilteredClaims(cache.filteredList, page, size);
};

export const createClaimListCache = () => ({
  paramsKey: '',
  filteredList: null,
});

export const clampInsuranceMinSearchDate = (date) => {
  if (!date) return date;
  const value = dayjs(date);
  const min = dayjs(INSURANCE_MIN_SEARCH_DATE);
  if (value.isBefore(min, 'day')) {
    return min.toDate();
  }
  return value.toDate();
};

/** @deprecated clampInsuranceMinSearchDate 사용 */
export const clampToDemoDateRange = clampInsuranceMinSearchDate;

export const clampInsuranceSearchDate = (date, loginId) => {
  if (!date || !hasInsuranceDateRestriction(loginId)) return date;
  return clampInsuranceMinSearchDate(date);
};

export const getEffectiveInsuranceSearchDates = (start, end, loginId) => ({
  startDate: clampInsuranceSearchDate(start, loginId),
  endDate: clampInsuranceSearchDate(end, loginId),
});

export const getInsuranceSearchRangeCalendarProps = (loginId) => {
  if (hasInsuranceDateRestriction(loginId)) {
    return {
      rangeStartDate: INSURANCE_MIN_SEARCH_DATE,
    };
  }
  return {};
};

export const getDefaultInsuranceSearchStartDate = (loginId, fallbackStartDate) => {
  if (hasInsuranceDateRestriction(loginId)) {
    return INSURANCE_MIN_SEARCH_DATE;
  }
  return fallbackStartDate;
};

export const getDefaultInsuranceSearchEndDate = (loginId, fallbackEndDate) =>
  fallbackEndDate;

export const getDefaultInsuranceSearchDates = (
  loginId,
  fallbackStartDate,
  fallbackEndDate
) => ({
  startDate: getDefaultInsuranceSearchStartDate(loginId, fallbackStartDate),
  endDate: getDefaultInsuranceSearchEndDate(loginId, fallbackEndDate),
});

/** @deprecated loginId 기반 getInsuranceSearchRangeCalendarProps 사용 */
export const getDemoRangeCalendarProps = (isDemoUser) =>
  isDemoUser
    ? {
        rangeStartDate: INSURANCE_MIN_SEARCH_DATE,
      }
    : {};
